import {
  ACCESS_COOKIE_NAME,
  ACCESS_TOKEN,
  CORRELATION_ID,
  PEQUITY_API_URL,
  PEQUITY_SOCKET_PROTOCOL,
  REFRESH_TOKEN,
} from '@/types';

export const isDev = import.meta.env.DEV;
export const isProd = import.meta.env.PROD;
const isFeatureTest = !!window.localStorage.getItem('TestServerPort');

// XXX This should be in the .env file.
export const getUrlBase = () => {
  const protocol = window.location.protocol + '//';
  const split = window.location.host.split(':');
  const newHost = split.length > 1 ? split[0] + ':8000' : split[0];
  return protocol + newHost;
};

export const URL_BASE = getUrlBase();

const getApiUrl = () => {
  if (!import.meta.env.DEV) {
    return `${window.location.origin}/api/`;
  }

  const url = new URL(PEQUITY_API_URL);

  if (isFeatureTest) {
    url.port = `${window.localStorage.getItem('TestServerPort')}`;
    const subdomain = window.localStorage.getItem('TestServerSubdomain');
    if (subdomain) {
      url.hostname = `${subdomain}.${url.hostname}`;
    }
  } else {
    const apiHostname = url.hostname;
    const browserHostname = window.location.hostname;
    if (browserHostname !== apiHostname && browserHostname.endsWith(`.${apiHostname}`)) {
      url.hostname = browserHostname;
    }
  }

  return url.href;
};

export const API_URL = getApiUrl();

export const WEBSOCKETS_URL = `${PEQUITY_SOCKET_PROTOCOL}://${new URL(URL_BASE).host}/ws`;

// Token helpers
export const tokenExpired = (token: string) => {
  if (!token) {
    return true;
  }
  const msExpiration = JSON.parse(atob(token.split('.')[1])).exp;
  const adjustedExpiration = msExpiration * 1000 - 15000;
  return adjustedExpiration < Date.now();
};

const setAccessCookie = (cookie: string) => {
  document.cookie = `${ACCESS_COOKIE_NAME}=${cookie}; path=/;`;
};

const IS_OIDC_SESSION = 'is_oidc_session';
const ISI_SESSION_ID = 'isi_session_id';
const OIDC_POST_LOGIN_REDIRECT = 'oidc_post_login_redirect';

export const setIsOidcSession = () => localStorage.setItem(IS_OIDC_SESSION, 'true');
export const clearIsOidcSession = () => localStorage.removeItem(IS_OIDC_SESSION);
export const isOidcSession = () => localStorage.getItem(IS_OIDC_SESSION) === 'true';

export const getIsiSessionId = () => localStorage.getItem(ISI_SESSION_ID);
export const setIsiSessionId = (id: string) => localStorage.setItem(ISI_SESSION_ID, id);
const clearIsiSessionId = () => localStorage.removeItem(ISI_SESSION_ID);

export const setOidcPostLoginRedirect = (path: string) => sessionStorage.setItem(OIDC_POST_LOGIN_REDIRECT, path);
export const consumeOidcPostLoginRedirect = (): string | null => {
  const path = sessionStorage.getItem(OIDC_POST_LOGIN_REDIRECT);
  sessionStorage.removeItem(OIDC_POST_LOGIN_REDIRECT);
  return path;
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN);
  setAccessCookie('');
  localStorage.removeItem(REFRESH_TOKEN);
  clearIsOidcSession();
  clearIsiSessionId();
};

type AimLogoutParams = {
  accessToken: string;
  logoutEndpoint: string;
  clientId: string;
  redirectUri?: string;
};

export const submitAimLogoutForm = ({ accessToken, logoutEndpoint, clientId, redirectUri }: AimLogoutParams) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = logoutEndpoint;

  const fields: Record<string, string> = {
    token_type_hint: 'access_token',
    token: accessToken,
    post_logout_redirect_uri: redirectUri ?? `${window.location.origin}/v3/logged-out`,
    trans_id: 'pequity-oidc',
    client_id: clientId,
  };

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
};

export const setAccessToken = (token: string) => {
  localStorage.setItem(ACCESS_TOKEN, token);
  setAccessCookie(`Bearer ${token}`);
};

export const setRefreshToken = (token: string) => {
  localStorage.setItem(REFRESH_TOKEN, token);
};

// Correlation Id helpers
export const setCorrelationId = (id: string | null) => {
  if (id === null) {
    return removeCorrelationId();
  }

  localStorage.setItem(CORRELATION_ID, id);
};

export const removeCorrelationId = () => {
  localStorage.removeItem(CORRELATION_ID);
};

export const getCorrelationId = () => {
  return localStorage.getItem(CORRELATION_ID) || null;
};
